import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, AlertCircle, Car, Home, Heart, Activity, Briefcase, FileText, Clock, ExternalLink, XCircle, PauseCircle, CheckCircle, Send, Edit, Settings, Mail, MailCheck, MailX, Loader2, History, RefreshCw, Eye, FileQuestion, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const policyTypeIcons = {
  auto: Car,
  casa: Home,
  vita: Heart,
  salute: Activity,
  attivita: Briefcase,
  rc_professionale: Briefcase
};

const policyTypeLabels = {
  auto: 'Veicolo',
  casa: 'Casa',
  vita: 'Vita',
  salute: 'Salute',
  attivita: 'Attività',
  rc_professionale: 'RC Prof.'
};

const Renewals = () => {
  const [policies, setPolicies] = useState([]);
  const [suspendedPolicies, setSuspendedPolicies] = useState([]);
  const [suspSort, setSuspSort] = useState('none'); // none | asc | desc (per Periodo Sospensione)
  const [nonClientAssets, setNonClientAssets] = useState([]);  // Nuovo: beni non clienti
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeFilter, setActiveFilter] = useState('30');
  const [renewalStatusFilter, setRenewalStatusFilter] = useState('all');
  
  // Dialog per aggiornare data scadenza
  const [editDateDialog, setEditDateDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [newEndDate, setNewEndDate] = useState('');
  
  // Email Automation Config
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    enabled: false,
    sender_email: 'no-reply@jvservice.it',
    sender_name: 'JV Service - Ufficio Rinnovi',
    days_before_veicolo: 30,
    days_before_casa: 45,
    days_before_attivita: 45,
    email_subject: 'Avviso Scadenza Polizza - JV Service',
    email_body: ''
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);
  
  // State per modal Reinvio e Storico Email
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedPolicyForEmail, setSelectedPolicyForEmail] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sendingResend, setSendingResend] = useState(false);
  const [previewEmail, setPreviewEmail] = useState({ subject: '', body: '' });
  
  // State per calendario interattivo
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [dayPoliciesModalOpen, setDayPoliciesModalOpen] = useState(false);
  const [selectedDayPolicies, setSelectedDayPolicies] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);

  // State per gestione ciclo vendita NON Clienti
  const [editAssetDateDialog, setEditAssetDateDialog] = useState(false);
  const [editingNonClientAsset, setEditingNonClientAsset] = useState(null);
  const [newAssetScadenza, setNewAssetScadenza] = useState('');
  const [editAssetNotesDialog, setEditAssetNotesDialog] = useState(false);
  const [editingAssetNotes, setEditingAssetNotes] = useState('');
  const [assetHistoryDialog, setAssetHistoryDialog] = useState(false);
  const [selectedAssetHistory, setSelectedAssetHistory] = useState([]);
  const [savingAssetData, setSavingAssetData] = useState(false);

  useEffect(() => {
    fetchData();
    fetchEmailConfig();
  }, []);
  
  const fetchEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/email-automation/config`);
      setEmailConfig(res.data);
    } catch (error) {
      // Config not found, use defaults
    }
  };
  
  const saveEmailConfig = async () => {
    setConfigLoading(true);
    try {
      await axios.put(`${API}/email-automation/config`, emailConfig);
      toast.success('Configurazione salvata');
      setConfigDialogOpen(false);
    } catch (error) {
      toast.error('Errore nel salvataggio della configurazione');
    } finally {
      setConfigLoading(false);
    }
  };
  
  const sendBatchEmails = async () => {
    setSendingBatch(true);
    try {
      const res = await axios.post(`${API}/email-automation/send-batch`);
      const totalSent = res.data.veicolo.sent + res.data.casa.sent + res.data.attivita.sent;
      const totalFailed = res.data.veicolo.failed + res.data.casa.failed + res.data.attivita.failed;
      
      if (totalSent > 0) {
        toast.success(`${totalSent} email inviate con successo`, {
          description: `Veicoli: ${res.data.veicolo.sent}, Case: ${res.data.casa.sent}, Attività: ${res.data.attivita.sent}`
        });
      }
      if (totalFailed > 0) {
        toast.warning(`${totalFailed} email non inviate`, {
          description: 'Controlla i log per maggiori dettagli'
        });
      }
      if (totalSent === 0 && totalFailed === 0) {
        toast.info('Nessuna email da inviare', {
          description: 'Tutte le polizze in scadenza sono già state notificate'
        });
      }
      
      // Refresh data to update email status
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'invio batch delle email');
    } finally {
      setSendingBatch(false);
    }
  };

  // Apre modal reinvio con anteprima email
  const openResendModal = async (policy) => {
    setSelectedPolicyForEmail(policy);
    
    // Genera anteprima email con i dati della polizza
    const subject = emailConfig.email_subject
      .replace('{client_name}', policy.client_name || '')
      .replace('{asset_description}', policy.asset_label || '')
      .replace('{expiry_date}', policy.end_date ? new Date(policy.end_date).toLocaleDateString('it-IT') : '')
      .replace('{policy_type}', policy.policy_type || '')
      .replace('{company}', policy.company || '');
    
    const body = emailConfig.email_body
      .replace('{client_name}', policy.client_name || '')
      .replace('{asset_description}', policy.asset_label || '')
      .replace('{expiry_date}', policy.end_date ? new Date(policy.end_date).toLocaleDateString('it-IT') : '')
      .replace('{days_remaining}', getDaysUntilExpiry(policy.end_date).toString())
      .replace('{policy_type}', policy.policy_type || '')
      .replace('{company}', policy.company || '');
    
    setPreviewEmail({ subject, body });
    setResendModalOpen(true);
  };

  // Invia email di reinvio
  const sendResendEmail = async () => {
    if (!selectedPolicyForEmail) return;
    
    setSendingResend(true);
    try {
      await axios.post(`${API}/policies/${selectedPolicyForEmail.id}/send-expiry-reminder`, {
        force_resend: true
      });
      toast.success('Email reinviata con successo', {
        description: `Inviata a ${selectedPolicyForEmail.client_email || 'email cliente'}`
      });
      setResendModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il reinvio dell\'email');
    } finally {
      setSendingResend(false);
    }
  };

  // Apre modal storico email
  const openHistoryModal = async (policy) => {
    setSelectedPolicyForEmail(policy);
    setLoadingHistory(true);
    setHistoryModalOpen(true);
    
    try {
      const res = await axios.get(`${API}/policies/${policy.id}/email-history`);
      setEmailHistory(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento dello storico');
      setEmailHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchData = async () => {
    try {
      const [policiesRes, suspendedRes, expiredRes, nonClientRes] = await Promise.all([
        axios.get(`${API}/policies?expiring_soon=true&expiring_days=45&include_expired=true`),
        axios.get(`${API}/policies/suspended`),
        // Carica anche polizze scadute non elaborate separatamente per una visione completa
        axios.get(`${API}/policies?status=attiva`),
        // Nuovo: Beni con scadenza copertura attuale entro 30 giorni (non clienti/prospect)
        axios.get(`${API}/assets/expiring-coverage?days=30`)
      ]);
      
      // Combina le polizze in scadenza con quelle scadute non ancora elaborate
      const allPolicies = [...policiesRes.data];
      
      // Aggiungi polizze scadute che non sono già nella lista
      expiredRes.data.forEach(policy => {
        const days = Math.ceil((new Date(policy.end_date) - new Date()) / (1000 * 60 * 60 * 24));
        // Se scaduta e non già presente
        if (days < 0 && !allPolicies.find(p => p.id === policy.id)) {
          allPolicies.push(policy);
        }
      });
      
      setPolicies(allPolicies);
      setSuspendedPolicies(suspendedRes.data);
      setNonClientAssets(nonClientRes.data);  // Salva beni non clienti
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysUntilExpiry = (endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(endDate);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyBadge = (days) => {
    if (days < 0) {
      // Polizza scaduta
      return (
        <Badge className="bg-red-600 text-white font-semibold">
          Scaduta {Math.abs(days)} gg fa
        </Badge>
      );
    } else if (days === 0) {
      // Scade oggi
      return (
        <Badge className="bg-red-600 text-white font-bold animate-pulse">
          OGGI
        </Badge>
      );
    } else if (days <= 7) {
      // Urgente (≤7 giorni)
      return (
        <Badge className="bg-red-100 text-red-700 font-semibold">
          {days} gg - URGENTE
        </Badge>
      );
    } else if (days <= 14) {
      // Imminente (8-14 giorni)
      return (
        <Badge className="bg-amber-100 text-amber-700 font-medium">
          {days} gg
        </Badge>
      );
    } else if (days <= 30) {
      // In scadenza a 30 giorni
      return (
        <Badge className="bg-orange-100 text-orange-700">
          {days} gg
        </Badge>
      );
    } else if (days <= 45) {
      // In scadenza a 45 giorni
      return (
        <Badge className="bg-blue-100 text-blue-700">
          {days} gg
        </Badge>
      );
    } else {
      // Prossima (>45 giorni)
      return (
        <Badge className="bg-slate-100 text-slate-600">
          {days} gg
        </Badge>
      );
    }
  };

  // Aggiorna lo stato renewal di una polizza
  const updateRenewalStatus = async (policyId, newStatus) => {
    try {
      await axios.patch(`${API}/policies/${policyId}/status`, {
        renewal_status: newStatus
      });
      // Aggiorna localmente
      setPolicies(prev => prev.map(p => 
        p.id === policyId ? { ...p, renewal_status: newStatus } : p
      ));
      const statusMessages = {
        'in_scadenza': 'Stato: In Scadenza',
        'attesa_preventivo': 'Stato: Attesa Preventivo',
        'comunicato_rinnovo': 'Stato: Comunicato Rinnovo',
        'sollecitato': 'Stato: Sollecitato'
      };
      toast.success(statusMessages[newStatus] || 'Stato aggiornato');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Aggiorna lo stato rinnovo di un asset (NON Clienti)
  const updateAssetRenewalStatus = async (assetId, newStatus) => {
    try {
      await axios.patch(`${API}/assets/${assetId}/renewal-status`, {
        renewal_status: newStatus
      });
      // Aggiorna localmente
      setNonClientAssets(prev => prev.map(asset => 
        asset.asset_id === assetId ? { ...asset, renewal_status: newStatus } : asset
      ));
      toast.success('Stato aggiornato');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  // Costanti colori e label per gli stati rinnovo
  const renewalStatusConfig = {
    in_scadenza: { label: 'In Scadenza', color: 'bg-amber-100 text-amber-700', border: 'border-amber-200' },
    attesa_preventivo: { label: 'Attesa Preventivo', color: 'bg-purple-100 text-purple-700', border: 'border-purple-200' },
    comunicato_rinnovo: { label: 'Comunicato', color: 'bg-blue-100 text-blue-700', border: 'border-blue-200' },
    sollecitato: { label: 'Sollecitato', color: 'bg-orange-100 text-orange-700', border: 'border-orange-200' },
  };

  // Aggiorna lo stato sospensione
  const updateSuspensionStatus = async (policyId, newStatus) => {
    try {
      await axios.patch(`${API}/policies/${policyId}/status`, {
        suspension_status: newStatus
      });
      if (newStatus === 'gestito') {
        fetchData();
        toast.success('Polizza riattivata e contrassegnata come gestita');
      } else {
        setSuspendedPolicies(prev => prev.map(p => 
          p.id === policyId ? { ...p, suspension_status: newStatus } : p
        ));
        toast.success('Stato aggiornato');
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // RESET CICLO VENDITA NON Clienti - Aggiorna data scadenza con reset automatico
  const openResetCycleDialog = (asset) => {
    setEditingNonClientAsset(asset);
    // Calcola nuova data: 1 anno dalla scadenza attuale (o da oggi se scaduta)
    const currentDate = new Date(asset.scadenza_copertura);
    const today = new Date();
    const baseDate = currentDate > today ? currentDate : today;
    const newDate = new Date(baseDate);
    newDate.setFullYear(newDate.getFullYear() + 1);
    setNewAssetScadenza(newDate.toISOString().split('T')[0]);
    setEditAssetDateDialog(true);
  };

  const handleResetAssetCycle = async () => {
    if (!editingNonClientAsset || !newAssetScadenza) return;
    setSavingAssetData(true);
    try {
      await axios.patch(`${API}/assets/${editingNonClientAsset.asset_id}/reset-cycle`, {
        new_scadenza_copertura: newAssetScadenza
      });
      
      // Aggiorna lo stato locale - reset completo
      setNonClientAssets(prev => prev.map(asset => 
        asset.asset_id === editingNonClientAsset.asset_id 
          ? { 
              ...asset, 
              scadenza_copertura: newAssetScadenza,
              renewal_status: 'in_scadenza',  // Reset automatico
              renewal_notes: '',  // Reset automatico
              days_until_expiry: Math.ceil((new Date(newAssetScadenza) - new Date()) / (1000 * 60 * 60 * 24))
            } 
          : asset
      ));
      
      setEditAssetDateDialog(false);
      setEditingNonClientAsset(null);
      setNewAssetScadenza('');
      toast.success('Ciclo vendita resettato! Stato e note riportati a "nuovo"', {
        description: 'Le note precedenti sono state archiviate'
      });
    } catch (error) {
      toast.error('Errore nel reset del ciclo vendita');
    } finally {
      setSavingAssetData(false);
    }
  };

  // Gestione note di rinnovo NON Clienti
  const openEditNotesDialog = (asset) => {
    setEditingNonClientAsset(asset);
    setEditingAssetNotes(asset.renewal_notes || '');
    setEditAssetNotesDialog(true);
  };

  const handleSaveAssetNotes = async () => {
    if (!editingNonClientAsset) return;
    setSavingAssetData(true);
    try {
      await axios.patch(`${API}/assets/${editingNonClientAsset.asset_id}/renewal-notes`, {
        renewal_notes: editingAssetNotes
      });
      
      // Aggiorna lo stato locale
      setNonClientAssets(prev => prev.map(asset => 
        asset.asset_id === editingNonClientAsset.asset_id 
          ? { ...asset, renewal_notes: editingAssetNotes } 
          : asset
      ));
      
      setEditAssetNotesDialog(false);
      setEditingNonClientAsset(null);
      setEditingAssetNotes('');
      toast.success('Note aggiornate');
    } catch (error) {
      toast.error('Errore nel salvataggio delle note');
    } finally {
      setSavingAssetData(false);
    }
  };

  // Visualizza storico note NON Clienti
  const openNotesHistory = (asset) => {
    setEditingNonClientAsset(asset);
    setSelectedAssetHistory(asset.notes_archive || []);
    setAssetHistoryDialog(true);
  };

  // Aggiorna la data di scadenza
  const updateEndDate = async () => {
    if (!editingPolicy || !newEndDate) return;
    try {
      await axios.patch(`${API}/policies/${editingPolicy.id}/status`, {
        new_end_date: newEndDate,
        suspension_status: 'gestito'
      });
      fetchData();
      setEditDateDialog(false);
      setEditingPolicy(null);
      setNewEndDate('');
      toast.success('Data scadenza aggiornata e polizza riattivata');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Filtra polizze in base al filtro attivo
  const filteredPolicies = policies.filter(policy => {
    const days = getDaysUntilExpiry(policy.end_date);
    const maxDays = parseInt(activeFilter);
    
    // Filtro per stato rinnovo
    if (renewalStatusFilter !== 'all') {
      const policyStatus = policy.renewal_status || 'in_scadenza';
      if (policyStatus !== renewalStatusFilter) return false;
    }
    
    if (activeFilter === 'scadute') {
      // Mostra polizze scadute (giorni negativi) non ancora rinnovate/archiviate
      return days < 0 && !['rinnovata', 'archiviata'].includes(policy.status?.toLowerCase());
    }
    
    if (activeFilter === 'sospensioni') {
      return false; // Gestito separatamente
    }
    
    if (activeFilter === '45_casa_attivita') {
      // 45 giorni No Veicoli: mostra SOLO polizze Casa, Attività e Salute
      const assetType = (policy.asset_type || policy.policy_type || '').toLowerCase();
      const isNoVeicolo = assetType === 'casa' || assetType === 'attivita' || assetType === 'salute';
      return days <= 45 && days >= 0 && isNoVeicolo;
    }
    
    // Per filtri numerici (7, 30) - include tutti i tipi di polizza
    return days <= maxDays && days >= 0;
  });

  // Conteggi
  const urgentCount = policies.filter(p => {
    const days = getDaysUntilExpiry(p.end_date);
    return days <= 7 && days >= 0;
  }).length;

  const thirtyDaysCount = policies.filter(p => {
    const days = getDaysUntilExpiry(p.end_date);
    return days <= 30 && days >= 0;
  }).length;

  // Conteggio 45 giorni No Veicoli: SOLO polizze Casa, Attività e Salute
  const fortyFiveCasaAttivitaCount = policies.filter(p => {
    const days = getDaysUntilExpiry(p.end_date);
    const assetType = (p.asset_type || p.policy_type || '').toLowerCase();
    const isNoVeicolo = assetType === 'casa' || assetType === 'attivita' || assetType === 'salute';
    return days <= 45 && days >= 0 && isNoVeicolo;
  }).length;

  const expiredCount = policies.filter(p => {
    const days = getDaysUntilExpiry(p.end_date);
    return days < 0 && !['rinnovata', 'archiviata'].includes(p.status?.toLowerCase());
  }).length;

  // Helper per generare URL di navigazione (per link nativi <a>)
  const getClientUrl = (clientId) => `/clients?open=${clientId}`;
  
  const getAssetUrl = (policy) => {
    if (policy.client_id && policy.asset_id) {
      return `/clients?open=${policy.client_id}&asset=${policy.asset_id}`;
    } else if (policy.client_id) {
      return `/clients?open=${policy.client_id}`;
    }
    return '#';
  };

  const expiryDates = policies.map(p => new Date(p.end_date));

  // Helper per raggruppare polizze per data di scadenza
  const getPoliciesByDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return policies.filter(p => {
      const policyDateStr = new Date(p.end_date).toISOString().split('T')[0];
      return policyDateStr === dateStr;
    });
  };

  // Genera i giorni del mese per il calendario custom
  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = domenica
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Giorni vuoti all'inizio
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Giorni del mese
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const policiesForDay = getPoliciesByDate(date);
      days.push({
        day,
        date,
        policies: policiesForDay,
        count: policiesForDay.length
      });
    }
    
    return days;
  };

  // Apri modal con polizze del giorno
  const openDayPoliciesModal = (dayData) => {
    if (dayData && dayData.count > 0) {
      setSelectedDayDate(dayData.date);
      setSelectedDayPolicies(dayData.policies);
      setDayPoliciesModalOpen(true);
    }
  };

  // Naviga mese precedente/successivo
  const navigateMonth = (direction) => {
    setCalendarMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="renewals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Scadenze e Rinnovi
          </h1>
          <p className="text-slate-600 mt-1">Monitora le polizze in scadenza e le sospensioni</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setConfigDialogOpen(true)}
            className="border-slate-300"
            data-testid="email-config-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurazione Email
          </Button>
          <Button
            onClick={sendBatchEmails}
            disabled={sendingBatch}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="send-batch-btn"
          >
            {sendingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Invia Promemoria
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 items-center">
        <Button
          variant={activeFilter === '7' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('7')}
          className={activeFilter === '7' ? 'bg-red-600 hover:bg-red-700' : ''}
          data-testid="filter-7-days"
        >
          <Clock className="w-4 h-4 mr-2" />
          Urgenti (7 giorni)
          <Badge className="ml-2 bg-white/20 text-inherit">{urgentCount}</Badge>
        </Button>
        <Button
          variant={activeFilter === '30' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('30')}
          className={activeFilter === '30' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          data-testid="filter-30-days"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          In Scadenza (30 giorni)
          <Badge className="ml-2 bg-white/20 text-inherit">{thirtyDaysCount}</Badge>
        </Button>
        <Button
          variant={activeFilter === '45_casa_attivita' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('45_casa_attivita')}
          className={activeFilter === '45_casa_attivita' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          data-testid="filter-45-casa-attivita"
        >
          <Home className="w-4 h-4 mr-2" />
          In Scadenza (45 gg, No Veicoli)
          <Badge className="ml-2 bg-white/20 text-inherit">{fortyFiveCasaAttivitaCount}</Badge>
        </Button>
        <Button
          variant={activeFilter === 'non_clienti' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('non_clienti')}
          className={activeFilter === 'non_clienti' ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'}
          data-testid="filter-non-clienti"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          NON Clienti (30 gg)
          <Badge className="ml-2 bg-white/20 text-inherit">{nonClientAssets.length}</Badge>
        </Button>
        <Button
          variant={activeFilter === 'scadute' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('scadute')}
          className={activeFilter === 'scadute' ? 'bg-red-700 hover:bg-red-800' : 'border-red-300 text-red-700 hover:bg-red-50'}
          data-testid="filter-expired"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Polizze Scadute
          <Badge className="ml-2 bg-white/20 text-inherit">{expiredCount}</Badge>
        </Button>
        <Button
          variant={activeFilter === 'sospensioni' ? 'default' : 'outline'}
          onClick={() => setActiveFilter('sospensioni')}
          className={activeFilter === 'sospensioni' ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}
          data-testid="filter-suspensions"
        >
          <PauseCircle className="w-4 h-4 mr-2" />
          Sospensioni
          <Badge className="ml-2 bg-white/20 text-inherit">{suspendedPolicies.length}</Badge>
        </Button>
        <div className="ml-auto">
          <Select value={renewalStatusFilter} onValueChange={setRenewalStatusFilter}>
            <SelectTrigger className="w-[200px] h-9" data-testid="renewal-status-filter">
              <SelectValue placeholder="Filtra per stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="in_scadenza">In Scadenza</SelectItem>
              <SelectItem value="attesa_preventivo">Attesa Preventivo</SelectItem>
              <SelectItem value="comunicato_rinnovo">Comunicato</SelectItem>
              <SelectItem value="sollecitato">Sollecitato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* NON Clienti Section - Scadenze copertura esterna */}
      {activeFilter === 'non_clienti' ? (
        <Card className="border-slate-200 border-l-4 border-l-cyan-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-cyan-500" />
              Scadenze NON Clienti ({nonClientAssets.length})
              <Badge className="ml-2 bg-cyan-100 text-cyan-700">Prospect e Coperture Esterne</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {nonClientAssets.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <ExternalLink className="w-12 h-12 mx-auto mb-2 opacity-30" />
                Nessun bene con scadenza copertura esterna nei prossimi 30 giorni
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo/Bene</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Urgenza</TableHead>
                      <TableHead className="text-center">Stato</TableHead>
                      <TableHead>Note Ciclo</TableHead>
                      <TableHead className="text-center">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nonClientAssets.map((asset) => {
                      const Icon = policyTypeIcons[asset.asset_type] || FileText;
                      const typeLabel = policyTypeLabels[asset.asset_type] || 'Altro';
                      const days = asset.days_until_expiry;
                      
                      // Colori urgenza
                      let urgencyClass = 'text-slate-600 bg-slate-100';
                      let urgencyLabel = `${days} gg`;
                      if (days <= 0) {
                        urgencyClass = 'text-red-800 bg-red-100 font-semibold';
                        urgencyLabel = days === 0 ? 'OGGI' : `Scaduta ${Math.abs(days)} gg fa`;
                      } else if (days <= 7) {
                        urgencyClass = 'text-red-700 bg-red-100';
                        urgencyLabel = `${days} gg - URGENTE`;
                      } else if (days <= 14) {
                        urgencyClass = 'text-amber-700 bg-amber-100';
                      }

                      // Stato rinnovo - usando stesso template delle polizze
                      const currentStatus = asset.renewal_status || 'in_scadenza';
                      const hasArchive = asset.notes_archive && asset.notes_archive.length > 0;

                      return (
                        <TableRow key={asset.asset_id} className="hover:bg-cyan-50/50">
                          <TableCell>
                            <Link
                              to={getClientUrl(asset.client_id)}
                              className="flex flex-col text-left hover:text-blue-600 transition-colors group no-underline"
                              data-testid={`non-client-link-${asset.client_id}`}
                            >
                              <span className="font-medium text-slate-900 group-hover:text-blue-600 flex items-center gap-1">
                                {asset.client_name}
                                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                              </span>
                              <span className="text-xs text-slate-500">{asset.client_code}</span>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Link
                              to={getAssetUrl({ client_id: asset.client_id, asset_id: asset.asset_id })}
                              className="flex flex-col text-left hover:text-blue-600 transition-colors group no-underline"
                              data-testid={`non-client-asset-link-${asset.asset_id}`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-cyan-600" />
                                <span className="text-slate-600 group-hover:text-blue-600">{typeLabel}</span>
                                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                              </div>
                              <span className="text-sm text-slate-800 font-medium group-hover:text-blue-600">{asset.asset_label}</span>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => openResetCycleDialog(asset)}
                              className="flex flex-col items-start hover:bg-cyan-100 px-2 py-1 rounded transition-colors cursor-pointer"
                              title="Clicca per modificare la data (resetta automaticamente lo stato e le note)"
                              data-testid={`edit-scadenza-${asset.asset_id}`}
                            >
                              <span className="font-medium text-slate-900">
                                {new Date(asset.scadenza_copertura).toLocaleDateString('it-IT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </span>
                              <span className="text-xs text-cyan-600 flex items-center gap-1">
                                <Edit className="w-3 h-3" /> Modifica
                              </span>
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge className={urgencyClass}>{urgencyLabel}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Select
                              value={currentStatus}
                              onValueChange={(newStatus) => updateAssetRenewalStatus(asset.asset_id, newStatus)}
                            >
                              <SelectTrigger 
                                className={`h-7 text-xs font-medium border px-2.5 rounded-full w-auto min-w-0 ${
                                  (renewalStatusConfig[currentStatus] || renewalStatusConfig.in_scadenza).color
                                } ${(renewalStatusConfig[currentStatus] || renewalStatusConfig.in_scadenza).border}`}
                                data-testid={`renewal-status-${asset.asset_id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="in_scadenza">In Scadenza</SelectItem>
                                <SelectItem value="attesa_preventivo">Attesa Preventivo</SelectItem>
                                <SelectItem value="comunicato_rinnovo">Comunicato</SelectItem>
                                <SelectItem value="sollecitato">Sollecitato</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <div className="flex flex-col gap-1">
                              {asset.renewal_notes ? (
                                <p className="text-sm text-slate-700 truncate" title={asset.renewal_notes}>
                                  {asset.renewal_notes}
                                </p>
                              ) : (
                                <span className="text-xs text-slate-400 italic">Nessuna nota</span>
                              )}
                              <button
                                onClick={() => openEditNotesDialog(asset)}
                                className="text-xs text-cyan-600 hover:text-cyan-800 flex items-center gap-1 w-fit"
                                data-testid={`edit-notes-${asset.asset_id}`}
                              >
                                <Edit className="w-3 h-3" />
                                {asset.renewal_notes ? 'Modifica' : 'Aggiungi nota'}
                              </button>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {hasArchive && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openNotesHistory(asset)}
                                  className="h-8 w-8 p-0 hover:bg-slate-100"
                                  title={`Storico: ${asset.notes_archive.length} cicli precedenti`}
                                  data-testid={`view-history-${asset.asset_id}`}
                                >
                                  <History className="w-4 h-4 text-slate-500" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openResetCycleDialog(asset)}
                                className="h-8 w-8 p-0 hover:bg-green-100"
                                title="Nuovo ciclo: Reset stato + Archivia note"
                                data-testid={`reset-cycle-${asset.asset_id}`}
                              >
                                <RefreshCw className="w-4 h-4 text-green-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeFilter === 'sospensioni' ? (
        <Card className="border-slate-200 border-l-4 border-l-orange-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <PauseCircle className="w-5 h-5 text-orange-500" />
              Polizze Sospese ({suspendedPolicies.length})
              <Badge className="ml-2 bg-orange-100 text-orange-700">Veicoli con fermo</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {suspendedPolicies.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Nessuna polizza sospesa
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-orange-50">
                      <TableHead className="font-semibold">Cliente</TableHead>
                      <TableHead className="font-semibold">Veicolo</TableHead>
                      <TableHead className="font-semibold">
                        <button
                          type="button"
                          onClick={() => setSuspSort((s) => (s === 'asc' ? 'desc' : s === 'desc' ? 'none' : 'asc'))}
                          className="flex items-center gap-1 hover:text-orange-700"
                          data-testid="susp-sort-periodo"
                        >
                          Periodo Sospensione
                          {suspSort === 'asc' ? <ArrowUp className="w-3.5 h-3.5" />
                            : suspSort === 'desc' ? <ArrowDown className="w-3.5 h-3.5" />
                            : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                        </button>
                      </TableHead>
                      <TableHead className="font-semibold">Scadenza Originale</TableHead>
                      <TableHead className="font-semibold text-center">Stato</TableHead>
                      <TableHead className="font-semibold text-center">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...suspendedPolicies].sort((a, b) => {
                      if (suspSort === 'none') return 0;
                      const da = new Date(a.suspension_end || 0).getTime();
                      const db = new Date(b.suspension_end || 0).getTime();
                      return suspSort === 'asc' ? da - db : db - da;
                    }).map((policy) => (
                      <TableRow key={policy.id} className="hover:bg-orange-50/50">
                        <TableCell>
                          <Link
                            to={getClientUrl(policy.client_id)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group no-underline"
                          >
                            <span className="font-medium text-slate-900 group-hover:text-blue-600">
                              {policy.client_name}
                            </span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={getAssetUrl(policy)}
                            className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group no-underline"
                          >
                            <Car className="w-4 h-4 text-blue-500" />
                            <span className="text-sm group-hover:text-blue-600">{policy.asset_label || '-'}</span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="font-medium">Dal:</span> {formatDateShort(policy.suspension_start)}<br/>
                            <span className="font-medium">Al:</span> {formatDateShort(policy.suspension_end)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {(() => {
                            const days = getDaysUntilExpiry(policy.end_date);
                            return (
                              <div className="space-y-1">
                                <span>{formatDateShort(policy.end_date)}</span>
                                <div>{getUrgencyBadge(days)}</div>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center">
                          <Select
                            value={policy.suspension_status || 'da_gestire'}
                            onValueChange={(value) => updateSuspensionStatus(policy.id, value)}
                          >
                            <SelectTrigger className="w-36" data-testid={`suspension-status-${policy.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="da_gestire">
                                <span className="flex items-center gap-2">
                                  <Clock className="w-3 h-3 text-orange-500" />
                                  Da Gestire
                                </span>
                              </SelectItem>
                              <SelectItem value="gestito">
                                <span className="flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-500" />
                                  Gestito
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingPolicy(policy);
                              setNewEndDate(policy.end_date?.split('T')[0] || '');
                              setEditDateDialog(true);
                            }}
                            data-testid={`edit-date-${policy.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Aggiorna Scadenza
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Main Content - Full Width Table */
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              {activeFilter === 'scadute' ? (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Polizze Scadute ({filteredPolicies.length})
                  <Badge className="ml-2 bg-red-100 text-red-700">Non rinnovate</Badge>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Polizze in Scadenza ({filteredPolicies.length})
                  {activeFilter === '45_casa_attivita' && (
                    <Badge className="ml-2 bg-blue-100 text-blue-700">Solo No Veicoli</Badge>
                  )}
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Caricamento...</div>
            ) : filteredPolicies.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Nessuna polizza per il filtro selezionato
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Cliente</TableHead>
                      <TableHead className="font-semibold">Compagnia</TableHead>
                      <TableHead className="font-semibold">Tipo/Bene</TableHead>
                      <TableHead className="font-semibold">Scadenza</TableHead>
                      <TableHead className="font-semibold text-center">Urgenza</TableHead>
                      <TableHead className="font-semibold text-center">Email Inviata</TableHead>
                      <TableHead className="font-semibold text-center">Stato Rinnovo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPolicies
                      .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
                      .map((policy) => {
                        const Icon = policyTypeIcons[policy.policy_type] || FileText;
                        const daysLeft = getDaysUntilExpiry(policy.end_date);
                        return (
                          <TableRow key={policy.id} className="hover:bg-slate-50">
                            <TableCell>
                              <Link
                                to={getClientUrl(policy.client_id)}
                                className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group no-underline"
                              >
                                <span className="font-medium text-slate-900 group-hover:text-blue-600">
                                  {policy.client_name}
                                </span>
                                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                              </Link>
                            </TableCell>
                            <TableCell className="text-slate-700">{policy.company}</TableCell>
                            <TableCell>
                              <Link
                                to={getAssetUrl(policy)}
                                className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group no-underline"
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  policy.policy_type === 'auto' ? 'bg-blue-100 text-blue-600' :
                                  policy.policy_type === 'casa' ? 'bg-green-100 text-green-600' :
                                  policy.policy_type === 'attivita' ? 'bg-purple-100 text-purple-600' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-700 group-hover:text-blue-600">
                                    {policyTypeLabels[policy.policy_type]}
                                  </p>
                                  {policy.asset_label && (
                                    <p className="text-xs text-slate-500 group-hover:text-blue-500">{policy.asset_label}</p>
                                  )}
                                </div>
                                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 ml-1" />
                              </Link>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-slate-900">{formatDateShort(policy.end_date)}</p>
                                <p className={`text-xs ${daysLeft <= 0 ? 'text-red-600 font-medium' : daysLeft <= 7 ? 'text-red-500' : 'text-slate-500'}`}>
                                  {daysLeft < 0 ? `Scaduta da ${Math.abs(daysLeft)} giorni` : 
                                   daysLeft === 0 ? 'Scade OGGI!' : 
                                   daysLeft === 1 ? 'Scade domani' : 
                                   `Tra ${daysLeft} giorni`}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {getUrgencyBadge(daysLeft)}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={() => openResendModal(policy)}
                                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                  data-testid={`resend-email-${policy.id}`}
                                  title={policy.expiry_reminder_sent ? `Reinvia (già inviata il ${policy.expiry_reminder_sent_at ? new Date(policy.expiry_reminder_sent_at).toLocaleDateString('it-IT') : '-'})` : 'Invia promemoria'}
                                >
                                  {policy.expiry_reminder_sent ? (
                                    <RefreshCw className="w-3 h-3" />
                                  ) : (
                                    <Send className="w-3 h-3" />
                                  )}
                                  <span>{policy.expiry_reminder_sent ? 'Reinvia' : 'Invia'}</span>
                                </button>
                                <button
                                  onClick={() => openHistoryModal(policy)}
                                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2 py-1 rounded transition-colors"
                                  data-testid={`email-history-${policy.id}`}
                                  title="Visualizza storico email"
                                >
                                  <History className="w-3 h-3" />
                                  <span>Storico</span>
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Select
                                value={policy.renewal_status || 'in_scadenza'}
                                onValueChange={(newStatus) => updateRenewalStatus(policy.id, newStatus)}
                              >
                                <SelectTrigger 
                                  className={`h-7 text-xs font-medium border px-2.5 rounded-full w-auto min-w-0 ${
                                    (renewalStatusConfig[policy.renewal_status || 'in_scadenza'] || renewalStatusConfig.in_scadenza).color
                                  } ${(renewalStatusConfig[policy.renewal_status || 'in_scadenza'] || renewalStatusConfig.in_scadenza).border}`}
                                  data-testid={`renewal-status-${policy.id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="in_scadenza">In Scadenza</SelectItem>
                                  <SelectItem value="attesa_preventivo">Attesa Preventivo</SelectItem>
                                  <SelectItem value="comunicato_rinnovo">Comunicato</SelectItem>
                                  <SelectItem value="sollecitato">Sollecitato</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card 
          className={`border-slate-200 border-l-4 border-l-red-500 cursor-pointer transition-all hover:shadow-md ${activeFilter === '7' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setActiveFilter('7')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Urgenti (7 giorni)</p>
                <p className="text-2xl font-bold text-slate-900">{urgentCount}</p>
              </div>
              <Clock className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-slate-200 border-l-4 border-l-amber-500 cursor-pointer transition-all hover:shadow-md ${activeFilter === '30' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setActiveFilter('30')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">In Scadenza (30 gg)</p>
                <p className="text-2xl font-bold text-slate-900">{thirtyDaysCount}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-slate-200 border-l-4 border-l-blue-500 cursor-pointer transition-all hover:shadow-md ${activeFilter === '45_casa_attivita' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setActiveFilter('45_casa_attivita')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">45 gg No Veicoli</p>
                <p className="text-2xl font-bold text-slate-900">{fortyFiveCasaAttivitaCount}</p>
                <p className="text-xs text-blue-600 mt-1">Solo immobili e aziende</p>
              </div>
              <Home className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-slate-200 border-l-4 border-l-orange-500 cursor-pointer transition-all hover:shadow-md ${activeFilter === 'sospensioni' ? 'ring-2 ring-orange-500' : ''}`}
          onClick={() => setActiveFilter('sospensioni')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Sospensioni</p>
                <p className="text-2xl font-bold text-slate-900">{suspendedPolicies.length}</p>
              </div>
              <PauseCircle className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-slate-200 border-l-4 border-l-cyan-500 cursor-pointer transition-all hover:shadow-md ${activeFilter === 'non_clienti' ? 'ring-2 ring-cyan-500' : ''}`}
          onClick={() => setActiveFilter('non_clienti')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">NON Clienti</p>
                <p className="text-2xl font-bold text-slate-900">{nonClientAssets.length}</p>
                <p className="text-xs text-cyan-600 mt-1">Prospect e coperture esterne</p>
              </div>
              <ExternalLink className="w-8 h-8 text-cyan-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Card Polizze Scadute */}
      <Card 
        className={`border-slate-200 border-l-4 border-l-red-700 cursor-pointer transition-all hover:shadow-md ${activeFilter === 'scadute' ? 'ring-2 ring-red-700' : ''}`}
        onClick={() => setActiveFilter('scadute')}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-700">Polizze Scadute (non rinnovate)</p>
              <p className="text-2xl font-bold text-red-900">{expiredCount}</p>
              <p className="text-xs text-slate-400 mt-1">Polizze con data fine antecedente a oggi</p>
            </div>
            <XCircle className="w-10 h-10 text-red-500 opacity-50" />
          </div>
        </CardContent>
      </Card>

      {/* Calendario Interattivo Full-Width */}
      <Card className="border-slate-200" data-testid="interactive-calendar">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-amber-500" />
              Calendario Scadenze
            </CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-slate-600">Urgente (1-7 gg)</span>
                <div className="w-3 h-3 rounded-full bg-amber-500 ml-3"></div>
                <span className="text-slate-600">In scadenza (8-30 gg)</span>
                <div className="w-3 h-3 rounded-full bg-blue-500 ml-3"></div>
                <span className="text-slate-600">Prossima (31+ gg)</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth(-1)}
                  className="h-8 w-8 p-0"
                  data-testid="calendar-prev-month"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-semibold text-slate-900 min-w-[140px] text-center">
                  {calendarMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth(1)}
                  className="h-8 w-8 p-0"
                  data-testid="calendar-next-month"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Intestazione giorni settimana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-sm font-semibold text-slate-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Griglia giorni */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayData, index) => {
              if (!dayData) {
                return <div key={`empty-${index}`} className="h-24 bg-slate-50 rounded-lg"></div>;
              }
              
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isToday = dayData.date.toDateString() === today.toDateString();
              const hasExpiries = dayData.count > 0;
              
              // Determina il colore in base all'urgenza delle polizze
              let urgencyColor = 'bg-blue-100 text-blue-700 border-blue-300';
              let badgeColor = 'bg-blue-500';
              if (hasExpiries) {
                const daysUntil = getDaysUntilExpiry(dayData.date);
                if (daysUntil <= 0) {
                  urgencyColor = 'bg-red-100 text-red-700 border-red-300';
                  badgeColor = 'bg-red-600';
                } else if (daysUntil <= 7) {
                  urgencyColor = 'bg-red-50 text-red-600 border-red-200';
                  badgeColor = 'bg-red-500';
                } else if (daysUntil <= 30) {
                  urgencyColor = 'bg-amber-50 text-amber-700 border-amber-200';
                  badgeColor = 'bg-amber-500';
                }
              }
              
              return (
                <div
                  key={dayData.day}
                  onClick={() => openDayPoliciesModal(dayData)}
                  className={`h-24 rounded-lg border transition-all flex flex-col p-2 ${
                    hasExpiries 
                      ? `${urgencyColor} cursor-pointer hover:shadow-md hover:scale-[1.02]` 
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                  data-testid={`calendar-day-${dayData.day}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-lg font-semibold ${isToday ? 'text-blue-600' : hasExpiries ? '' : 'text-slate-700'}`}>
                      {dayData.day}
                    </span>
                    {hasExpiries && (
                      <span className={`${badgeColor} text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center`}>
                        {dayData.count}
                      </span>
                    )}
                  </div>
                  {hasExpiries && (
                    <div className="mt-auto">
                      <p className="text-xs font-medium truncate">
                        {dayData.count} Polizz{dayData.count === 1 ? 'a' : 'e'}
                      </p>
                      <p className="text-[10px] opacity-75 truncate">
                        Clicca per dettagli
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal Polizze del Giorno */}
      <Dialog open={dayPoliciesModalOpen} onOpenChange={setDayPoliciesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-amber-500" />
              Polizze in Scadenza - {selectedDayDate?.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </DialogTitle>
            <DialogDescription>
              {selectedDayPolicies.length} polizz{selectedDayPolicies.length === 1 ? 'a' : 'e'} in scadenza in questa data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {selectedDayPolicies.map((policy) => {
              const Icon = policyTypeIcons[policy.policy_type] || FileText;
              return (
                <div
                  key={policy.id}
                  className="p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        policy.policy_type === 'auto' ? 'bg-blue-100 text-blue-600' :
                        policy.policy_type === 'casa' ? 'bg-green-100 text-green-600' :
                        policy.policy_type === 'attivita' ? 'bg-purple-100 text-purple-600' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <Link
                          to={getClientUrl(policy.client_id)}
                          className="font-semibold text-slate-900 hover:text-blue-600 flex items-center gap-1"
                          onClick={() => setDayPoliciesModalOpen(false)}
                        >
                          {policy.client_name}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                        <p className="text-sm text-slate-600">{policy.company}</p>
                        <Link
                          to={getAssetUrl(policy)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          onClick={() => setDayPoliciesModalOpen(false)}
                        >
                          {policyTypeLabels[policy.policy_type]}: {policy.asset_label || '-'}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={`${
                        (renewalStatusConfig[policy.renewal_status || 'in_scadenza'] || renewalStatusConfig.in_scadenza).color
                      }`}>
                        {(renewalStatusConfig[policy.renewal_status || 'in_scadenza'] || renewalStatusConfig.in_scadenza).label}
                      </Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDayPoliciesModalOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog per aggiornare data scadenza */}
      <Dialog open={editDateDialog} onOpenChange={setEditDateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Aggiorna Data Scadenza
            </DialogTitle>
            <DialogDescription>
              Aggiorna la data di scadenza della polizza dopo il periodo di sospensione.
            </DialogDescription>
          </DialogHeader>
          
          {editingPolicy && (
            <div className="space-y-4 mt-4">
              <div className="bg-orange-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-500">Polizza:</span> <span className="font-medium">{editingPolicy.policy_number || <span className="text-amber-600 italic">N. Polizza da inserire</span>}</span></p>
                <p><span className="text-slate-500">Cliente:</span> <span className="font-medium">{editingPolicy.client_name}</span></p>
                <p><span className="text-slate-500">Veicolo:</span> <span className="font-medium">{editingPolicy.asset_label || '-'}</span></p>
                <p><span className="text-slate-500">Sospensione:</span> <span className="font-medium">{formatDateShort(editingPolicy.suspension_start)} - {formatDateShort(editingPolicy.suspension_end)}</span></p>
                <p><span className="text-slate-500">Scadenza attuale:</span> <span className="font-medium">{formatDateShort(editingPolicy.end_date)}</span></p>
              </div>
              
              <div className="space-y-2">
                <Label>Nuova Data Scadenza *</Label>
                <DateInput
                  value={newEndDate}
                  onChange={(val) => setNewEndDate(val)}
                  data-testid="new-end-date-input"
                  placeholder="gg/mm/aaaa"
                />
                <p className="text-xs text-slate-500">
                  Calcola la nuova scadenza sommando i giorni di fermo alla data originale.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDateDialog(false)}>
                  Annulla
                </Button>
                <Button onClick={updateEndDate} data-testid="save-new-date">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salva e Riattiva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Configurazione Automazione Email */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurazione Automazione Email
            </DialogTitle>
            <DialogDescription>
              Configura i parametri per l'invio automatico delle email di promemoria scadenza.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Abilitazione */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Automazione Attiva</Label>
                <p className="text-sm text-slate-500">Abilita l'invio automatico giornaliero delle email</p>
              </div>
              <Switch
                checked={emailConfig.enabled}
                onCheckedChange={(checked) => setEmailConfig({...emailConfig, enabled: checked})}
                data-testid="email-automation-enabled"
              />
            </div>
            
            {/* Orario di Invio */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Orario di Invio Giornaliero
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">
                    L'invio automatico verrà eseguito ogni giorno a questo orario (fuso orario: Europe/Rome)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    type="time"
                    value={emailConfig.daily_send_time || "09:00"}
                    onChange={(e) => setEmailConfig({...emailConfig, daily_send_time: e.target.value})}
                    className="w-32 text-center font-mono text-lg"
                    data-testid="daily-send-time"
                  />
                </div>
              </div>
            </div>
            
            {/* Mittente */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email Mittente</Label>
                <Input 
                  value={emailConfig.sender_email}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_email: e.target.value})}
                  placeholder="no-reply@jvservice.it"
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-slate-500">Forzato: no-reply@jvservice.it</p>
              </div>
              <div className="space-y-2">
                <Label>Nome Mittente</Label>
                <Input 
                  value={emailConfig.sender_name}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_name: e.target.value})}
                  placeholder="JV Service - Ufficio Rinnovi"
                />
              </div>
            </div>
            
            {/* Intervalli Giorni */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Giorni Prima della Scadenza</Label>
              <p className="text-sm text-slate-500">Configura quanti giorni prima della scadenza inviare la notifica</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-blue-600" />
                    <Label>Veicoli</Label>
                  </div>
                  <Input 
                    type="number"
                    min={1}
                    max={90}
                    value={emailConfig.days_before_veicolo}
                    onChange={(e) => setEmailConfig({...emailConfig, days_before_veicolo: parseInt(e.target.value) || 30})}
                    data-testid="days-before-veicolo"
                  />
                  <p className="text-xs text-slate-500">giorni prima</p>
                </div>
                <div className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-green-600" />
                    <Label>Case</Label>
                  </div>
                  <Input 
                    type="number"
                    min={1}
                    max={90}
                    value={emailConfig.days_before_casa}
                    onChange={(e) => setEmailConfig({...emailConfig, days_before_casa: parseInt(e.target.value) || 45})}
                    data-testid="days-before-casa"
                  />
                  <p className="text-xs text-slate-500">giorni prima</p>
                </div>
                <div className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-amber-600" />
                    <Label>Attività</Label>
                  </div>
                  <Input 
                    type="number"
                    min={1}
                    max={90}
                    value={emailConfig.days_before_attivita}
                    onChange={(e) => setEmailConfig({...emailConfig, days_before_attivita: parseInt(e.target.value) || 45})}
                    data-testid="days-before-attivita"
                  />
                  <p className="text-xs text-slate-500">giorni prima</p>
                </div>
              </div>
            </div>
            
            {/* Template Email */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                <Label className="text-base font-medium">Template Email</Label>
              </div>
              
              {/* Oggetto */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Oggetto Email
                  <span className="text-xs font-normal text-slate-500">(supporta variabili)</span>
                </Label>
                <Input 
                  value={emailConfig.email_subject}
                  onChange={(e) => setEmailConfig({...emailConfig, email_subject: e.target.value})}
                  placeholder="Scadenza Polizza - {asset_description} - JV Service"
                  data-testid="email-subject"
                />
              </div>
              
              {/* Corpo del Messaggio */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Corpo del Messaggio
                  <span className="text-xs font-normal text-slate-500">(personalizzabile)</span>
                </Label>
                <Textarea 
                  value={emailConfig.email_body}
                  onChange={(e) => setEmailConfig({...emailConfig, email_body: e.target.value})}
                  placeholder="Gentile {client_name},

le ricordiamo che la sua polizza relativa a {asset_description} scadrà il giorno {expiry_date}.

La invitiamo a contattarci per procedere con il rinnovo."
                  rows={10}
                  className="font-mono text-sm leading-relaxed"
                  data-testid="email-body"
                />
              </div>
              
              {/* Variabili disponibili */}
              <div className="bg-slate-50 rounded-lg p-4 border">
                <p className="text-sm font-medium text-slate-700 mb-2">📝 Variabili disponibili:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <code className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{'{client_name}'}</code>
                    <span className="text-slate-500">Nome cliente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{'{asset_description}'}</code>
                    <span className="text-slate-500">Targa/Indirizzo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{'{expiry_date}'}</code>
                    <span className="text-slate-500">Data scadenza</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{'{days_remaining}'}</code>
                    <span className="text-slate-500">Giorni rimanenti</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{'{policy_type}'}</code>
                    <span className="text-slate-500">Tipo polizza</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{'{company}'}</code>
                    <span className="text-slate-500">Compagnia</span>
                  </div>
                </div>
              </div>
              
              {/* Preview firma */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">✉️ Firma Email (inclusa automaticamente):</p>
                <div className="text-sm text-slate-600 space-y-1">
                  <p className="font-semibold text-slate-800">JV SERVICE SRLS</p>
                  <p>📍 Via Fabio Filzi, 39 L, 59100 Prato (PO) – Italy</p>
                  <p>📱 Cell: +39 338 225 3074</p>
                  <p>✉️ Email: assicurazioni@jvservice.it</p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={saveEmailConfig} disabled={configLoading}>
              {configLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Salva Configurazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reinvio Email */}
      <Dialog open={resendModalOpen} onOpenChange={setResendModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Reinvia Email Promemoria
            </DialogTitle>
            <DialogDescription>
              Anteprima dell'email che verrà inviata al cliente
            </DialogDescription>
          </DialogHeader>
          
          {selectedPolicyForEmail && (
            <div className="space-y-4 mt-4">
              {/* Info Polizza */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Cliente:</span>
                    <span className="ml-2 font-medium">{selectedPolicyForEmail.client_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <span className="ml-2 font-medium">{selectedPolicyForEmail.client_email || 'N/D'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Bene:</span>
                    <span className="ml-2 font-medium">{selectedPolicyForEmail.asset_label}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Scadenza:</span>
                    <span className="ml-2 font-medium text-red-600">
                      {selectedPolicyForEmail.end_date ? new Date(selectedPolicyForEmail.end_date).toLocaleDateString('it-IT') : 'N/D'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Anteprima Email */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white px-4 py-2 text-sm font-medium">
                  📧 Anteprima Email
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Oggetto:</Label>
                    <p className="font-medium">{previewEmail.subject}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Corpo:</Label>
                    <div className="bg-slate-50 rounded p-3 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {previewEmail.body}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Warning se già inviata */}
              {selectedPolicyForEmail.expiry_reminder_sent && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-700">Attenzione: Email già inviata</p>
                    <p className="text-amber-600">
                      Questa polizza ha già ricevuto un promemoria il {selectedPolicyForEmail.expiry_reminder_sent_at ? new Date(selectedPolicyForEmail.expiry_reminder_sent_at).toLocaleDateString('it-IT') : 'precedentemente'}.
                      Procedendo, verrà inviata una nuova email.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setResendModalOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={sendResendEmail} 
              disabled={sendingResend}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingResend ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Conferma e Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Storico Email */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Storico Email - {selectedPolicyForEmail?.asset_label || 'Polizza'}
            </DialogTitle>
            <DialogDescription>
              Cronologia di tutte le email inviate per questa polizza
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">Caricamento storico...</span>
              </div>
            ) : emailHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nessuna email inviata per questa polizza</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Data e Ora</TableHead>
                      <TableHead className="font-semibold">Oggetto</TableHead>
                      <TableHead className="font-semibold">Destinatario</TableHead>
                      <TableHead className="font-semibold text-center">Stato</TableHead>
                      <TableHead className="font-semibold text-center">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailHistory.map((email, index) => (
                      <TableRow key={email.id || index}>
                        <TableCell className="text-sm">
                          {email.sent_at ? new Date(email.sent_at).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate">
                          {email.subject || 'N/D'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {email.recipient || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {email.status === 'sent' || email.status === 'success' ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                              <MailCheck className="w-3 h-3 mr-1" />
                              Inviata
                            </Badge>
                          ) : email.status === 'failed' ? (
                            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                              <MailX className="w-3 h-3 mr-1" />
                              Fallita
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700">
                              {email.status || 'N/D'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 px-2">
                                <Eye className="w-3 h-3 mr-1" />
                                Dettagli
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>Dettagli Email</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <Label className="text-slate-500">Data invio:</Label>
                                    <p className="font-medium">
                                      {email.sent_at ? new Date(email.sent_at).toLocaleString('it-IT') : '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-slate-500">Destinatario:</Label>
                                    <p className="font-medium">{email.recipient || '-'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-slate-500">Tipo invio:</Label>
                                    <p className="font-medium capitalize">{email.type || 'automatico'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-slate-500">Stato:</Label>
                                    <p className="font-medium capitalize">{email.status || '-'}</p>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-slate-500">Oggetto:</Label>
                                  <p className="font-medium bg-slate-50 p-2 rounded mt-1">{email.subject || '-'}</p>
                                </div>
                                <div>
                                  <Label className="text-slate-500">Corpo del messaggio:</Label>
                                  <div className="bg-slate-50 p-3 rounded mt-1 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                                    {email.body || 'Contenuto non disponibile'}
                                  </div>
                                </div>
                                {email.error && (
                                  <div className="bg-red-50 border border-red-200 rounded p-3">
                                    <Label className="text-red-600">Errore:</Label>
                                    <p className="text-red-700 text-sm mt-1">{email.error}</p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Reset Ciclo Vendita - Modifica Data Scadenza NON Clienti */}
      <Dialog open={editAssetDateDialog} onOpenChange={setEditAssetDateDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-green-600" />
              Nuovo Ciclo Vendita
            </DialogTitle>
            <DialogDescription asChild className="text-amber-600 bg-amber-50 p-3 rounded-lg mt-2">
              <div>
                <strong>Attenzione:</strong> Modificando la data scadenza, il sistema eseguirà automaticamente:
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>Reset dello <strong>Stato Rinnovo</strong> → "In Scadenza"</li>
                  <li>Archiviazione delle <strong>Note correnti</strong></li>
                  <li>Il record apparirà come nuovo da gestire</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          {editingNonClientAsset && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Cliente</p>
                <p className="font-semibold">{editingNonClientAsset.client_name}</p>
                <p className="text-sm text-slate-600">{editingNonClientAsset.asset_label}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Scadenza Attuale</Label>
                  <p className="font-medium text-red-600">
                    {new Date(editingNonClientAsset.scadenza_copertura).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Stato Attuale</Label>
                  <Badge className={
                    (renewalStatusConfig[editingNonClientAsset.renewal_status || 'in_scadenza'] || renewalStatusConfig.in_scadenza).color
                  }>
                    {(renewalStatusConfig[editingNonClientAsset.renewal_status || 'in_scadenza'] || renewalStatusConfig.in_scadenza).label}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label htmlFor="newScadenza">Nuova Data Scadenza *</Label>
                <DateInput
                  id="newScadenza"
                  value={newAssetScadenza}
                  onChange={(val) => setNewAssetScadenza(val)}
                  className="mt-1"
                  data-testid="input-new-scadenza"
                  placeholder="gg/mm/aaaa"
                />
                <p className="text-xs text-slate-500 mt-1">
                  La data viene automaticamente impostata a +1 anno dalla scadenza attuale
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditAssetDateDialog(false);
              setEditingNonClientAsset(null);
              setNewAssetScadenza('');
            }}>
              Annulla
            </Button>
            <Button 
              onClick={handleResetAssetCycle}
              disabled={!newAssetScadenza || savingAssetData}
              className="bg-green-600 hover:bg-green-700"
              data-testid="btn-confirm-reset-cycle"
            >
              {savingAssetData ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Conferma Reset Ciclo</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Modifica Note Rinnovo NON Clienti */}
      <Dialog open={editAssetNotesDialog} onOpenChange={setEditAssetNotesDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-600" />
              Note Ciclo Rinnovo
            </DialogTitle>
            <DialogDescription>
              Aggiungi o modifica le note per questo ciclo di vendita. Le note verranno archiviate automaticamente al prossimo reset.
            </DialogDescription>
          </DialogHeader>
          
          {editingNonClientAsset && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Cliente</p>
                <p className="font-semibold">{editingNonClientAsset.client_name}</p>
                <p className="text-sm text-slate-600">{editingNonClientAsset.asset_label}</p>
              </div>
              
              <div>
                <Label htmlFor="renewalNotes">Note</Label>
                <Textarea
                  id="renewalNotes"
                  value={editingAssetNotes}
                  onChange={(e) => setEditingAssetNotes(e.target.value)}
                  placeholder="Es: Contattato il 15/01, chiede preventivo entro fine mese..."
                  className="mt-1 min-h-[120px]"
                  data-testid="textarea-renewal-notes"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditAssetNotesDialog(false);
              setEditingNonClientAsset(null);
              setEditingAssetNotes('');
            }}>
              Annulla
            </Button>
            <Button 
              onClick={handleSaveAssetNotes}
              disabled={savingAssetData}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="btn-save-notes"
            >
              {savingAssetData ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</>
              ) : (
                'Salva Note'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Storico Note Archiviate NON Clienti */}
      <Dialog open={assetHistoryDialog} onOpenChange={setAssetHistoryDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Storico Cicli Precedenti
            </DialogTitle>
            <DialogDescription>
              Note e stati dei cicli di vendita precedenti per questo prospect.
            </DialogDescription>
          </DialogHeader>
          
          {editingNonClientAsset && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Cliente</p>
                <p className="font-semibold">{editingNonClientAsset.client_name}</p>
                <p className="text-sm text-slate-600">{editingNonClientAsset.asset_label}</p>
              </div>
              
              {selectedAssetHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  Nessun ciclo precedente archiviato
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedAssetHistory.slice().reverse().map((entry, idx) => (
                    <div 
                      key={idx} 
                      className="border border-slate-200 rounded-lg p-4 bg-white hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-xs">
                          Ciclo #{selectedAssetHistory.length - idx}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          Archiviato: {new Date(entry.date).toLocaleDateString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <p className="text-xs text-slate-500">Scadenza</p>
                          <p className="font-medium text-sm">
                            {entry.scadenza_precedente ? 
                              new Date(entry.scadenza_precedente).toLocaleDateString('it-IT') : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Stato Finale</p>
                          <Badge className={
                            entry.stato_precedente === 'comunicato_rinnovo' ? 'bg-blue-100 text-blue-700' :
                            entry.stato_precedente === 'attesa_preventivo' ? 'bg-purple-100 text-purple-700' :
                            entry.stato_precedente === 'sollecitato' ? 'bg-orange-100 text-orange-700' :
                            entry.stato_precedente === 'convertito' ? 'bg-green-100 text-green-700' :
                            entry.stato_precedente === 'rifiutato' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {entry.stato_precedente === 'comunicato_rinnovo' ? 'Comunicato' :
                             entry.stato_precedente === 'attesa_preventivo' ? 'Attesa Prev.' :
                             entry.stato_precedente === 'sollecitato' ? 'Sollecitato' :
                             entry.stato_precedente === 'convertito' ? 'Convertito' :
                             entry.stato_precedente === 'rifiutato' ? 'Rifiutato' :
                             entry.stato_precedente || 'In Scadenza'}
                          </Badge>
                        </div>
                      </div>
                      
                      {entry.note ? (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <p className="text-xs text-slate-500 mb-1">Note</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{entry.note}</p>
                        </div>
                      ) : (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <p className="text-xs text-slate-400 italic">Nessuna nota per questo ciclo</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAssetHistoryDialog(false);
              setEditingNonClientAsset(null);
              setSelectedAssetHistory([]);
            }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Renewals;
